/**
 * The capacity formula, its utilisation counterpart and a worked example (R3.5,
 * R6.2, R5.8's transparency; UI spec §5.5).
 *
 * Distinct from the shared `FormulaTransparencyBlock`, which states all three
 * Manpower formulas in one paragraph for screens that only need to disclose them.
 * This screen is where capacity is *entered*, so it earns the arithmetic in full,
 * and above all it earns a worked example.
 *
 * ## The example is computed, not written
 *
 * The figures come from a real row of the register and are multiplied out here, so
 * the example cannot drift from the formula above it or from the assumptions in
 * force. A hardcoded example is a comment that rots: it keeps claiming
 * `450 × 4 h × 0.95 = 1,710 h` after an administrator changes the availability
 * factor to 0.80, and a reader who checks it against the table then trusts the
 * table less rather than more.
 *
 * The privacy note is not decoration either: R18.13 fixes the planning granularity
 * at role, Region and period, and the absence of employee-level tracking is a
 * deliberate design constraint someone reading a headcount column will wonder about.
 */
import { useTranslation } from 'react-i18next';
import { InfoIcon } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { formatHours } from '../shared/manpower-format';

/** The one row's figures the worked example multiplies out. */
export interface CapacityFormulaExample {
  readonly roleLabel: string;
  readonly periodLabel: string;
  readonly headcount: number;
  readonly availableDays: number;
  readonly productiveHoursPerWorkday: number;
  readonly availabilityFactor: number;
  readonly capacityHours: number;
}

export interface CapacityFormulaBlockProps {
  /** A real row to work through, or `undefined` when the register has none to use. */
  readonly example: CapacityFormulaExample | undefined;
}

export function CapacityFormulaBlock({
  example,
}: CapacityFormulaBlockProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <section aria-labelledby="ci-formula-heading" className="flex flex-col gap-2">
      <h2
        id="ci-formula-heading"
        className="text-label text-[10.5px] font-semibold tracking-wide uppercase"
      >
        {t('manpower.capacityInputs.formula.heading')}
      </h2>

      <Card className="border-line bg-bg-subtle gap-2 rounded-[var(--radius-control)] p-4">
        <FormulaLine
          label={t('manpower.capacityInputs.formula.capacityLabel')}
          body={t('manpower.capacityInputs.formula.capacity')}
        />
        <FormulaLine
          label={t('manpower.capacityInputs.formula.utilisationLabel')}
          body={t('manpower.capacityInputs.formula.utilisation')}
        />
        {example !== undefined && (
          <FormulaLine
            label={t('manpower.capacityInputs.formula.exampleLabel', {
              role: example.roleLabel,
              period: example.periodLabel,
            })}
            body={t('manpower.capacityInputs.formula.example', {
              headcount: example.headcount,
              days: example.availableDays,
              fteDays: formatHours(example.headcount * example.availableDays),
              hours: example.productiveHoursPerWorkday.toFixed(2),
              availability: example.availabilityFactor.toFixed(2),
              capacity: formatHours(example.capacityHours),
            })}
          />
        )}
      </Card>

      {/* UI spec §3.8's information tier, for a constraint rather than a warning. */}
      <p className="flex items-start gap-2 rounded-[var(--radius-control)] border border-[var(--a-info-border)] bg-[var(--a-info-bg)] p-2 text-[11.5px] leading-[1.5] text-[var(--a-info-txt)]">
        <InfoIcon className="mt-[1px] size-3.5 shrink-0" aria-hidden />
        <span>{t('manpower.capacityInputs.formula.privacyNote')}</span>
      </p>
    </section>
  );
}

function FormulaLine({
  label,
  body,
}: {
  readonly label: string;
  readonly body: string;
}): React.JSX.Element {
  return (
    <p className="text-muted-foreground text-[11.5px] leading-[1.6]">
      <strong className="text-txt font-semibold">{label}</strong> {body}
    </p>
  );
}
